import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircleNotch, ArrowRight, ArrowLeft, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "login") { await login(email, password); nav("/"); }
      else if (mode === "register") { await register(email, password, name); nav("/"); }
      else { await api.forgotPassword(email); setSent(true); }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const heading = mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password";
  const sub = mode === "login" ? "Sign in to continue to your strategy room."
    : mode === "register" ? "Start mapping your challenges in minutes."
    : "Enter your email and we'll send you a secure reset link.";

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-foreground px-12 py-14 text-background lg:flex">
        <div className="font-serif text-2xl tracking-tight">Executive Strategy Room</div>
        <div>
          <h1 className="font-serif text-5xl leading-[1.05]">Managing complexity, made visible.</h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-background/70">
            Map a hard challenge as a system of interlocking problems, then weigh solution systems that resolve several at once.
          </p>
        </div>
        <div className="text-xs text-background/50">Your challenges are private to your account.</div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="mb-6 font-serif text-lg tracking-tight text-foreground">Executive Strategy Room</div>
            <h2 data-testid="auth-heading" className="font-serif text-3xl">{heading}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{sub}</p>
          </div>

          {mode === "forgot" && sent ? (
            <div data-testid="reset-sent" className="border border-border bg-card p-5">
              <CheckCircle size={26} weight="fill" style={{ color: "hsl(var(--tag-preference))" }} />
              <p className="mt-3 text-sm leading-relaxed text-foreground/90">If an account exists for <strong>{email}</strong>, a reset link is on its way. It expires in 1 hour.</p>
              <button data-testid="back-to-login-btn" onClick={() => { setMode("login"); setSent(false); }} className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline underline-offset-2"><ArrowLeft size={14} /> Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
                  <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-sm border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground" placeholder="Jane Doe" />
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Email</label>
                <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-sm border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground" placeholder="you@company.com" />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Password</label>
                  <div className="relative">
                    <input data-testid="auth-password-input" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-sm border border-border bg-background px-3 py-2.5 pr-10 text-sm outline-none focus:border-foreground" placeholder="••••••••" />
                    <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {mode === "login" && (
                    <div className="mt-2 text-right">
                      <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(null); }} className="text-xs font-medium text-foreground underline underline-offset-2 hover:text-primary">Forgot your password?</button>
                    </div>
                  )}
                </div>
              )}

              {error && <p data-testid="auth-error" className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}

              <button data-testid="auth-submit-btn" type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-50">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />}
                {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
              </button>
            </form>
          )}

          {mode === "forgot" && !sent && (
            <button data-testid="back-to-login-btn" onClick={() => { setMode("login"); setError(null); }} className="mt-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={14} /> Back to sign in</button>
          )}

          {mode !== "forgot" && (
            <p className="mt-6 text-sm text-muted-foreground">
              {mode === "login" ? "No account yet? " : "Already have an account? "}
              <button data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }} className="font-medium text-foreground underline underline-offset-2">
                {mode === "login" ? "Create one" : "Sign in"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
